import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Search, RefreshCw, LogOut, Package, Truck, CheckCircle2, Euro, CalendarDays, Archive } from 'lucide-react';
import { fetchOrders, fetchStats, apiError } from './adminApi';
import { STATUS_META, ACTIVE_STATUSES, ARCHIVED_STATUSES, fmtMoney, fmtDate, itemsCount, customerLabel, hasAddress } from './orderMeta';
import OrderDetail from './OrderDetail';

const STAT_CARDS = [
  { key: 'to_ship', label: 'Da spedire', icon: Package, color: '#ffb347' },
  { key: 'shipped', label: 'Spediti', icon: Truck, color: '#ff69b4', from: 'by_status' },
  { key: 'delivered', label: 'Consegnati', icon: CheckCircle2, color: '#39d98a', from: 'by_status' },
  { key: 'revenue', label: 'Incasso', icon: Euro, color: '#4da8ff', money: true },
  { key: 'today', label: 'Oggi', icon: CalendarDays, color: '#c084fc' },
  { key: 'archived', label: 'Archiviati', icon: Archive, color: '#ffffff' },
];

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, color: '#fff' };
  return <span className="adm-badge" style={{ '--c': meta.color }} data-testid={`status-badge-${status}`}>{meta.label}</span>;
};

const OrderRow = ({ order, onOpen }) => (
  <tr className={`adm-row ${order.archived ? 'is-archived' : ''}`} onClick={() => onOpen(order)} data-testid={`order-row-${order.order_number}`}>
    <td><strong className="adm-mono">{order.order_number}</strong>{order.source === 'free' && <span className="adm-tag">test</span>}</td>
    <td className="adm-muted">{fmtDate(order.created_at)}</td>
    <td>
      <div className="adm-cust">{customerLabel(order)}</div>
      <div className="adm-muted adm-small">{order.customer?.email || (hasAddress(order) ? order.shipping_address.city : 'indirizzo mancante')}</div>
    </td>
    <td className="adm-center">{itemsCount(order)}</td>
    <td className="adm-right"><strong>{fmtMoney(order.total, order.currency)}</strong></td>
    <td><StatusBadge status={order.status} /></td>
    <td className="adm-muted adm-small">{order.shipment?.tracking_number ? `${order.shipment.carrier_name} · ${order.shipment.tracking_number}` : '—'}</td>
  </tr>
);

const AdminDashboard = ({ admin, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [view, setView] = useState('active');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o] = await Promise.all([fetchStats(), fetchOrders({ status, q, view, limit: 100 })]);
      setStats(s);
      setOrders(o.orders);
      setTotal(o.total);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [status, q, view]);

  useEffect(() => {
    const id = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(id);
  }, [load, q]);

  const matchesFilter = (o) => (status ? o.status === status : view === 'all' || (view === 'archived') === Boolean(o.archived));

  const onOrderChanged = (updated) => {
    setSelected(updated);
    setOrders((prev) => (matchesFilter(updated) ? prev.map((o) => (o.id === updated.id ? updated : o)) : prev.filter((o) => o.id !== updated.id)));
    fetchStats().then(setStats).catch(() => {});
  };

  return (
    <div className="adm-shell" data-testid="admin-dashboard">
      <header className="adm-top">
        <div className="adm-brand">
          <img src="/images/beautykit-logo.png" alt="BeautyKit" />
          <span>Ordini &amp; Spedizioni</span>
        </div>
        <div className="adm-top-actions">
          <span className="adm-muted adm-small" data-testid="admin-user-email">{admin?.email}</span>
          <button className="adm-icon-btn" onClick={load} title="Aggiorna" data-testid="admin-refresh"><RefreshCw size={16} /></button>
          <button className="adm-icon-btn" onClick={onLogout} title="Esci" data-testid="admin-logout"><LogOut size={16} /></button>
        </div>
      </header>

      <section className="adm-stats" data-testid="admin-stats">
        {STAT_CARDS.map(({ key, label, icon: Icon, color, from, money }) => {
          const raw = stats ? (from ? stats[from][key] : stats[key]) : null;
          return (
            <div key={key} className="adm-stat" style={{ '--c': color }} data-testid={`stat-${key}`}>
              <Icon size={18} />
              <strong>{raw === null ? '…' : money ? fmtMoney(raw) : raw}</strong>
              <span>{label}</span>
            </div>
          );
        })}
      </section>

      <section className="adm-toolbar">
        <div className="adm-tabs" role="tablist">
          <button className={`adm-tab ${status === '' && view === 'active' ? 'active' : ''}`} onClick={() => { setStatus(''); setView('active'); }} data-testid="filter-active">
            Attivi {stats ? `(${stats.active})` : ''}
          </button>
          {(view === 'archived' ? ARCHIVED_STATUSES : ACTIVE_STATUSES).map((s) => (
            <button key={s} className={`adm-tab ${status === s ? 'active' : ''}`} style={{ '--c': STATUS_META[s].color }} onClick={() => setStatus(s)} data-testid={`filter-${s}`}>
              {STATUS_META[s].label} {stats ? `(${stats.by_status[s]})` : ''}
            </button>
          ))}
          <button className={`adm-tab adm-tab-archive ${status === '' && view === 'archived' ? 'active' : ''}`} onClick={() => { setStatus(''); setView('archived'); }} data-testid="filter-archived">
            <Archive size={13} /> Archivio {stats ? `(${stats.archived})` : ''}
          </button>
        </div>
        <label className="adm-search">
          <Search size={15} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca numero ordine, cliente, email, tracking…" data-testid="orders-search" />
        </label>
      </section>

      <section className="adm-table-wrap">
        <table className="adm-table" data-testid="orders-table">
          <thead>
            <tr><th>Ordine</th><th>Data</th><th>Cliente</th><th className="adm-center">Pezzi</th><th className="adm-right">Totale</th><th>Stato</th><th>Spedizione</th></tr>
          </thead>
          <tbody>
            {orders.map((o) => <OrderRow key={o.id} order={o} onOpen={setSelected} />)}
          </tbody>
        </table>
        {!loading && orders.length === 0 && <p className="adm-empty" data-testid="orders-empty">{view === 'archived' && !status ? 'Nessun ordine archiviato.' : 'Nessun ordine da gestire: tutto consegnato!'}</p>}
        {loading && <p className="adm-empty">Caricamento…</p>}
        {!loading && total > orders.length && <p className="adm-empty">Mostrati {orders.length} di {total} ordini — affina la ricerca.</p>}
      </section>

      {selected && <OrderDetail order={selected} onClose={() => setSelected(null)} onChanged={onOrderChanged} />}
    </div>
  );
};

export default AdminDashboard;
